import * as graphql from 'generated/graphql';
import { orderBy, sortBy } from 'lodash';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import SidebarSectionHeader from './SectionHeader';
import FeedbackResultQuestion from 'feedback/results/FeedbackResultQuestion';
import { Tags } from '@treams/treams-ui';

function FeedbackRequestResultsSmall(props: { requestID: string }) {
	const request = graphql.useFeedbackResultsQuery({ variables: { id: props.requestID } });

	const responses = sortBy(
		request.data?.feedbackRequest.responses.edges.map(({ node }) => node),
		(response) => `${response.recipient.firstName} ${response.recipient.lastName}`,
	);

	return (
		<>
			{!request.data && <FeedbackResultQuestion.Skeleton />}
			{request.data &&
				responses &&
				request.data?.feedbackRequest.questions.map((question) => (
					<Box mb={3} key={question.id}>
						<FeedbackResultQuestion
							request={request.data!.feedbackRequest as graphql.FeedbackRequest}
							question={question as graphql.FeedbackRequestQuestion}
							response={undefined}
							responses={responses as graphql.FeedbackResponse[]}
							small
						/>
					</Box>
				))}
		</>
	);
}

export default function SidebarFeedbackSection(props: { userID: string }) {
	const { t } = useTranslation();
	const feedback = graphql.useFeedbackRequestsQuery({
		variables: { userID: props.userID, last: 10, isArchived: false },
	});

	const options = orderBy(
		feedback.data?.user.feedbackRequests.edges ?? [],
		({ node }) => node.createdAt,
		'desc',
	).map(({ node }) => ({
		title: node.title || t('feedback.incoming.card.title'),
		value: node.id,
	}));

	const [isOpen, setIsOpen] = useState(false);
	const [selectedFeedback, setSelectedFeedback] = useState<undefined | string>(undefined);

	useEffect(() => {
		if (!options || options.length < 1) {
			return;
		}

		if (selectedFeedback === undefined) {
			setSelectedFeedback(options[0]!.value);
		}
	}, [options, selectedFeedback]);

	return (
		<>
			<SidebarSectionHeader isOpen={isOpen} onClick={() => setIsOpen(!isOpen)}>
				{t('conversation.sidebar.feedback.title')}
			</SidebarSectionHeader>

			{isOpen && (
				<>
					{options && (
						<Box mb={3}>
							<Tags.Item.Select
								label={t('conversation.sidebar.feedback.request.label')}
								options={options}
								selectedValue={selectedFeedback!}
								onChange={setSelectedFeedback}
							/>
						</Box>
					)}

					{selectedFeedback && <FeedbackRequestResultsSmall requestID={selectedFeedback} />}
				</>
			)}
		</>
	);
}
